// @flow
import { fetchWonItems, type FetchWonItemsParams } from '../api/wonItems';
import { getAuthToken, getBidderId, LOG_OUT_BIDDER } from './user';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';
import ms from 'ms';

/* Action Types */
export const LOAD_WON_ITEMS_FAIL = 'la/ui/wonItems/LOAD_FAIL';
export const LOAD_WON_ITEMS_REQUEST = 'la/ui/wonItems/LOAD_REQUEST';
export const LOAD_WON_ITEMS_SUCCESS = 'la/ui/wonItems/LOAD_SUCCESS';
export const CHANGE_WON_ITEMS_PAGE = 'la/ui/wonItems/CHANGE_WON_ITEMS_PAGE';
export const CHANGE_WON_ITEMS_PAGE_SIZE = 'la/ui/wonItems/CHANGE_WON_ITEMS_PAGE_SIZE';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    items: [],
    loaded: null,
    loading: false,
    page: 1,
    pageSize: 10,
    totalItems: 0,
};

export type State = {|
    ...typeof DEFAULT_STATE,
    loaded: ?number,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_WON_ITEMS_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_WON_ITEMS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_WON_ITEMS_SUCCESS:
            return {
                ...state,
                items: cloneDeep(action.payload.catalogs),
                loaded: action.meta.actionTime,
                loading: false,
                totalItems: action.payload.count,
            };
        case CHANGE_WON_ITEMS_PAGE_SIZE:
            return {
                ...state,
                pageSize: action.payload,
            };
        case CHANGE_WON_ITEMS_PAGE:
            return {
                ...state,
                page: action.payload,
            };
        case LOG_OUT_BIDDER:
            return {
                ...DEFAULT_STATE,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.wonItems;

const loadedSelector = createSelector(stateSelector, state => state.loaded);
const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getTotalWonItemsCount = createSelector(stateSelector, state => state.totalItems) || 0;
export const getWonItems = createSelector(stateSelector, state => state.items) || [];
export const getWonItemsPage = createSelector(stateSelector, state => state.page) || 1;
export const getWonItemsPageSize = createSelector(stateSelector, state => state.pageSize) || 10;
export const areWonItemsLoaded = loadedSelector;

const shouldFetchWonItems = createSelector([loadedSelector, loadingSelector], (loaded, loading) => {
    if (loaded) {
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    return !loading;
});

/* ACTION CREATORS */
type FetchWonItemsIfNeededParams = {
    force?: boolean,
    page?: number,
    pageSize?: number,
};

export const fetchWonItemsIfNeeded = ({ force = false, page, pageSize }: FetchWonItemsIfNeededParams) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();

    if (shouldFetchWonItems(state) || force) {
        page = page || getWonItemsPage(state);
        pageSize = pageSize || getWonItemsPageSize(state);
        await changeWonItemsPage({ force: true, page, pageSize })(dispatch, getState);
    } else {
        return Promise.resolve();
    }
};

type ChangeWonItemsPageParams = {
    force?: boolean,
    page: number,
    pageSize: number,
};
export const changeWonItemsPage = ({ force, page, pageSize }: ChangeWonItemsPageParams) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const oldPage = getWonItemsPage(state);
    const oldPageSize = getWonItemsPageSize(state);
    let dirty = false;

    if (oldPage !== page) {
        dirty = true;
        dispatch({
            payload: Number(page),
            type: CHANGE_WON_ITEMS_PAGE,
        });
    }

    if (oldPageSize !== pageSize) {
        dirty = true;
        dispatch({
            payload: Number(pageSize),
            type: CHANGE_WON_ITEMS_PAGE_SIZE,
        });
    }

    if (dirty || force) {
        try {
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const bidderId = getBidderId(state);

            dispatch({
                meta: { actionTime: Date.now() },
                type: LOAD_WON_ITEMS_REQUEST,
            });
            const response = await fetchWonItems(
                ({ authToken, bidderId, deployment, page, pageSize }: FetchWonItemsParams)
            );
            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.data,
                type: LOAD_WON_ITEMS_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: LOAD_WON_ITEMS_FAIL,
            });
        }
    }
};
